// src/admin/components/CategoryManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Drawer, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, MenuItem, Paper, Select, Switch, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, Alert, CircularProgress, Stack, Chip, Tooltip, Pagination,
  InputAdornment, Collapse, useTheme, useMediaQuery, Divider, Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Category as CategoryIcon, ChevronRight,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon, Cancel as CancelIcon, CloudUpload as CloudUploadIcon,
  Star as StarIcon, StarBorder as StarBorderIcon, Search as SearchIcon,
  FilterList as FilterListIcon, Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { Category, CategoryFormData, CategoryWithSubcategories } from '../../../enums/product';
import { CategoryService } from './categoryService';
import { useAuth } from '../../../contexts/AuthContext';
import ToastNotification from '../common/Toastnotification';
import {
  useCategoriesPaginated,
  useCategoryStats,
  useCategoryMutations,
} from '../../../hooks/useCategories';

const MAX_STARRED_CATEGORIES = 6;
const CATEGORIES_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ── LoadingOverlay ────────────────────────────────────────────────────────────
interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) {
        setRect(wrapperRef.current.getBoundingClientRect());
      }
    };

    measure();

    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);

    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading categories...
        </Typography>
      </Box>
    </Box>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const CategoryManagement: React.FC = () => {
  // CHANGE: was const { currentUser } = useAuth();
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();

  // ── Permission flags ───────────────────────────────────────────────────────
  const canView = isSuperAdmin || hasPermission('category.view');
  const canCreate = isSuperAdmin || hasPermission('category.create');
  const canEdit = isSuperAdmin || hasPermission('category.edit');
  const canDelete = isSuperAdmin || hasPermission('category.delete');
  const canToggleStatus = isSuperAdmin || hasPermission('category.edit');
  // Starred toggle maps to category.edit — it's an inline field update
  const canToggleStarred = isSuperAdmin || hasPermission('category.edit');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '', description: '', parentId: '', isActive: true, sortOrder: '0',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [starredLoading, setStarredLoading] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // Delete confirmation dialog state
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // typeFilter removed — always 'all' passed to service
  const [starredFilter, setStarredFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    isFetching: categoriesIsFetching,
    isError: categoriesError,
    error: categoriesErrorObj,
  } = useCategoriesPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    type: 'all', // type filter removed from UI; always pass 'all'
    starred: starredFilter,
    pageSize: CATEGORIES_PER_PAGE,
  });

  const { data: statsData, isLoading: statsLoading } = useCategoryStats();

  const { create, update, remove, toggleStatus, toggleStarred } = useCategoryMutations();

  // ── Derived values from query results ────────────────────────────────────
  const categories = categoriesData?.categories ?? [];
  const totalCategories = categoriesData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCategories / CATEGORIES_PER_PAGE);
  const globalStats = statsData ?? {
    totalMainCategories: 0,
    totalSubcategories: 0,
    totalStarred: 0,
    totalActive: 0,
    totalInactive: 0,
  };
  const loading = categoriesLoading || categoriesIsFetching;
  const saveAction = drawerMode === 'add' ? 'Creating Category…' : 'Updating Category…';

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const handleStarredFilterChange = (value: string) => {
    setStarredFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setStarredFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || starredFilter !== 'all';
  const activeFiltersCount = [
    searchQuery !== '',
    statusFilter !== 'all',
    starredFilter !== 'all',
  ].filter(Boolean).length;

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ name: '', description: '', parentId: '', isActive: true, sortOrder: '0' });
    setSelectedCategory(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleAddCategory = (parentId?: string) => {
    if (!canCreate) return;
    resetForm();
    if (parentId) setFormData(prev => ({ ...prev, parentId }));
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    if (!canEdit) return;
    setFormData({
      name: category.name,
      description: category.description || '',
      parentId: category.parentId || '',
      isActive: category.isActive,
      sortOrder: category.sortOrder?.toString() || '0',
    });
    setSelectedCategory(category);
    setImagePreview(category.imageUrl || '');
    setImageFile(null);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => { setDrawerOpen(false); resetForm(); };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteCategory = (category: Category) => {
    if (!canDelete) return; // ADD
    setDeleteTarget(category);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      resetToFirstPage();
      showToast('Category deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete category: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Toggle status ──────────────────────────────────────────────────────────
  const handleToggleStatus = async (category: Category) => {
    if (!canToggleStatus) return;
    try {
      await toggleStatus.mutateAsync({
        categoryId: category.id!,
        isActive: !category.isActive,
      });
      resetToFirstPage();
      showToast(
        `Category ${!category.isActive ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
    } catch (err: any) {
      showToast(`Failed to update category status: ${err.message || 'Unknown error'}`, 'error');
    }
  };

  // ── Toggle starred ─────────────────────────────────────────────────────────
  const handleToggleStarred = async (category: Category) => {
    if (!canToggleStarred) return;
    if (category.parentId) { showToast('Only main categories can be starred', 'warning'); return; }
    try {
      setStarredLoading(category.id!);
      if (!category.isStarred && globalStats.totalStarred >= MAX_STARRED_CATEGORIES) {
        showToast(
          `Maximum ${MAX_STARRED_CATEGORIES} categories can be starred. Please unstar a category first.`,
          'warning'
        );
        return;
      }
      await toggleStarred.mutateAsync({
        categoryId: category.id!,
        isStarred: !category.isStarred,
      });
      resetToFirstPage();
      showToast(
        !category.isStarred ? 'Category starred successfully' : 'Category unstarred successfully',
        'success'
      );
    } catch (err: any) {
      showToast(`Failed to update starred status: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setStarredLoading('');
    }
  };

  // ── Image helpers ─────────────────────────────────────────────────────────
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
      if (file.size > 5 * 1024 * 1024) { showToast('Image size should be less than 5MB', 'error'); return; }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = async () => {
    if (selectedCategory?.imageUrl && drawerMode === 'edit') {
      try {
        setImageUploadLoading(true);
        await CategoryService.deleteImage(selectedCategory.id!, selectedCategory.imageUrl);
        setImagePreview('');
        setImageFile(null);
        showToast('Image removed successfully', 'success');
      } catch (err: any) {
        showToast(`Failed to delete image: ${err.message || 'Unknown error'}`, 'error');
      } finally {
        setImageUploadLoading(false);
      }
    } else {
      setImagePreview('');
      setImageFile(null);
    }
  };

  // Image upload is shown only for main categories (no parentId)
  const shouldShowImageUpload = !formData.parentId;

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { showToast('Category name is required', 'error'); return; }

    // Image is mandatory for main categories
    if (!formData.parentId) {
      const hasExistingImage = drawerMode === 'edit' && (imagePreview || selectedCategory?.imageUrl);
      const hasNewImage = !!imageFile;
      if (!hasExistingImage && !hasNewImage) {
        showToast('Category image is required for main categories', 'error');
        return;
      }
    }

    try {
      setSaveLoading(true);
      if (drawerMode === 'add') {
        await create.mutateAsync({
          formData: { ...formData, sortOrder: '0' }, // sortOrder always 0
          userId: currentUser?.uid || '',
          imageFile,
        });
        showToast('Category created successfully', 'success');
      } else if (selectedCategory) {
        await update.mutateAsync({
          categoryId: selectedCategory.id!,
          formData: { ...formData, sortOrder: '0' }, // sortOrder always 0
          imageFile,
        });
        showToast('Category updated successfully', 'success');
      }
      resetToFirstPage();
      setDrawerOpen(false);
      resetForm();
    } catch (err: any) {
      showToast(`Failed to save category: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'parentId' && value && (imagePreview || imageFile)) handleRemoveImage();
  };

  // ADD HERE ↓
  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CategoryIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              You don't have permission to view categories. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && categories.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading categories...</Typography>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      ref={containerRef}
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
        gap: 0,
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <CategoryIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Category Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Organize your products with categories and subcategories
              </Typography>
            </Box>
          </Stack>
          {canCreate && (  // ADD wrap
            <Button onClick={() => handleAddCategory()} variant="contained" startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
              Add Category
            </Button>
          )}
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Main Categories
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {statsLoading ? <CircularProgress size={20} /> : globalStats.totalMainCategories}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {statsLoading ? '—' : `${globalStats.totalSubcategories} subcategories total`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Subcategories
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {statsLoading ? <CircularProgress size={20} /> : globalStats.totalSubcategories}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {statsLoading ? '—' : `across ${globalStats.totalMainCategories} main categories`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Starred (max {MAX_STARRED_CATEGORIES})
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {statsLoading ? <CircularProgress size={20} /> : `${globalStats.totalStarred} / ${MAX_STARRED_CATEGORIES}`}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {statsLoading ? '—' : `${MAX_STARRED_CATEGORIES - globalStats.totalStarred} slots remaining`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Active Categories
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {statsLoading ? <CircularProgress size={20} /> : globalStats.totalActive}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>
                      {statsLoading ? '—' : `${globalStats.totalActive} Active`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {statsLoading ? '—' : `${globalStats.totalInactive} Inactive`}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by name, description... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>Clear</Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                {/* Status filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)} label="Status">
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {/* Starred filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Starred</InputLabel>
                    <Select value={starredFilter} onChange={(e) => handleStarredFilterChange(e.target.value)} label="Starred">
                      <MenuItem value="all">All Categories</MenuItem>
                      <MenuItem value="starred">Starred Only</MenuItem>
                      <MenuItem value="not-starred">Not Starred</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {/* Result count */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${categories.length} matching`
                        : `Showing ${categories.length} on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

        {categories.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Categories Match Your Filters' : 'No Categories Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : "You haven't created any categories yet. Start by adding your first category."}
                </Typography>
                {hasActiveFilters ? (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
                ) : (
                  canCreate && (  // ADD wrap
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleAddCategory()}>
                      Add Category
                    </Button>
                  )
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {categories.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 700, md: 1000 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40px"></TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Description</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                    <TableCell>Status</TableCell>
                    {/* Sort Order column removed */}
                    {(canCreate || canEdit || canToggleStatus || canToggleStarred || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <React.Fragment key={category.id}>
                      {/* Main Category Row */}
                      <TableRow sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <TableCell>
                          {canToggleStarred && (
                            <Tooltip title={category.isStarred
                              ? 'Remove from starred'
                              : `Mark as starred (${globalStats.totalStarred}/${MAX_STARRED_CATEGORIES})`}>
                              <IconButton
                                onClick={() => handleToggleStarred(category)}
                                disabled={starredLoading === category.id}
                                size="small"
                                sx={{ color: category.isStarred ? '#ff9800' : 'inherit', '&:hover': { color: category.isStarred ? '#f57c00' : '#ff9800' } }}
                              >
                                {starredLoading === category.id
                                  ? <CircularProgress size={16} />
                                  : category.isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {category.imageUrl ? (
                              <Box component="img" src={category.imageUrl} alt={category.name}
                                sx={{ width: { xs: 30, sm: 40 }, height: { xs: 30, sm: 40 }, borderRadius: 1, objectFit: 'cover' }} />
                            ) : (
                              <CategoryIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            )}
                            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.5}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                {category.name}
                              </Typography>
                              {category.isStarred && (
                                <Chip label="Starred" size="small" variant="outlined"
                                  sx={{ ...getSoftChipSx('warning', isDark), height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Stack>
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">{category.description || '-'}</Typography>
                        </TableCell>

                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Chip label="Main Category" size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={category.isActive ? 'Active' : 'Inactive'}
                            size="small" variant="outlined"
                            sx={getSoftChipSx(category.isActive ? 'success' : 'default', isDark)}
                          />
                        </TableCell>

                        {/* Sort Order cell removed */}

                        {(canCreate || canEdit || canToggleStatus || canDelete) && (  // ADD wrap
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">

                              {canCreate && (  // ADD wrap — "Add Subcategory" button
                                <Tooltip title="Add Subcategory">
                                  <IconButton onClick={() => handleAddCategory(category.id)} size="small" color="primary">
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canEdit && (  // ADD wrap
                                <Tooltip title="Edit Category">
                                  <IconButton onClick={() => handleEditCategory(category)} size="small" color="warning">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canToggleStatus && (  // ADD wrap
                                <Tooltip title={category.isActive ? 'Deactivate' : 'Activate'}>
                                  <IconButton onClick={() => handleToggleStatus(category)} size="small" color="info">
                                    {category.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canDelete && (  // ADD wrap
                                <Tooltip title="Delete Category">
                                  <IconButton onClick={() => handleDeleteCategory(category)} disabled={deleteLoading === category.id} size="small" color="error">
                                    {deleteLoading === category.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              )}

                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Subcategory Rows */}
                      {category.subcategories.map((subcategory) => (
                        <TableRow
                          key={subcategory.id}
                          sx={{
                            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'grey.50',
                            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'grey.100' },
                          }}
                        >
                          <TableCell></TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: { xs: 1, sm: 4 } }}>
                              <ChevronRight fontSize="small" color="disabled" />
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{subcategory.name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="body2" color="text.secondary">{subcategory.description || '-'}</Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Chip label="Subcategory" size="small" variant="outlined" sx={getSoftChipSx('secondary', isDark)} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={subcategory.isActive ? 'Active' : 'Inactive'}
                              size="small" variant="outlined"
                              sx={getSoftChipSx(subcategory.isActive ? 'success' : 'default', isDark)}
                            />
                          </TableCell>
                          {/* Sort Order cell removed */}
                          {(canEdit || canToggleStatus || canDelete) && (  // ADD wrap
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">

                                {canEdit && (  // ADD wrap
                                  <Tooltip title="Edit Subcategory">
                                    <IconButton onClick={() => handleEditCategory(subcategory)} size="small" color="warning">
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {canToggleStatus && (  // ADD wrap
                                  <Tooltip title={subcategory.isActive ? 'Deactivate' : 'Activate'}>
                                    <IconButton onClick={() => handleToggleStatus(subcategory)} size="small" color="info">
                                      {subcategory.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {canDelete && (  // ADD wrap
                                  <Tooltip title="Delete Subcategory">
                                    <IconButton onClick={() => handleDeleteCategory(subcategory)} disabled={deleteLoading === subcategory.id} size="small" color="error">
                                      {deleteLoading === subcategory.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}

                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalCategories === 0 ? 0 : (currentPage - 1) * CATEGORIES_PER_PAGE + 1}–
                    {Math.min(currentPage * CATEGORIES_PER_PAGE, totalCategories)} of {totalCategories}
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)} page={currentPage} onChange={handlePageChange}
                    color="primary" size={isMobile ? 'small' : 'medium'} showFirstButton showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Add/Edit Drawer ── */}
      <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 500 } } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h6">{drawerMode === 'add' ? 'Add New Category' : 'Edit Category'}</Typography>
            <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Category Name" name="name" value={formData.name}
                    onChange={handleInputChange} required placeholder="Enter category name" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Description" name="description" value={formData.description}
                    onChange={handleInputChange} multiline rows={3} placeholder="Enter category description" />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Parent Category</InputLabel>
                    <Select name="parentId" value={formData.parentId} onChange={handleSelectChange} label="Parent Category">
                      <MenuItem value="">None (Main Category)</MenuItem>
                      {categories.map(category => (
                        <MenuItem key={category.id} value={category.id} disabled={selectedCategory?.id === category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Image upload — mandatory for main categories */}
                {shouldShowImageUpload && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: '1px dashed',
                        borderColor: !imagePreview && !imageFile ? 'error.main' : 'divider',
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2">Category Image</Typography>
                        <Typography variant="subtitle2" color="error">*</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Required. Upload an image for this main category (max 5 MB).
                      </Typography>
                      {imagePreview ? (
                        <Box>
                          <Box component="img" src={imagePreview} alt="Category preview"
                            sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, mb: 1 }} />
                          <Button onClick={handleRemoveImage} disabled={imageUploadLoading} variant="outlined" color="error" size="small"
                            startIcon={imageUploadLoading ? <CircularProgress size={16} /> : <DeleteIcon />} fullWidth>
                            Remove Image
                          </Button>
                        </Box>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth
                          color="error">
                          Upload Image
                          <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
                        </Button>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Sort Order field removed from UI */}

                {/* <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="isActive" checked={formData.isActive} onChange={handleInputChange} />}
                    label="Active"
                  />
                </Grid> */}
              </Grid>
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<CancelIcon />}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}>
                {drawerMode === 'add' ? 'Create Category' : 'Update Category'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          Delete {deleteTarget?.parentId ? 'Subcategory' : 'Category'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay ───────────────────────────────────── */}
      <Backdrop
        open={saveLoading || deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {deleteDialogLoading ? 'Deleting Category…' : saveAction}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default CategoryManagement;